import React, {useCallback, useEffect} from 'react'
import {useBlocker} from 'react-router-dom'
import type {BlockerFunction} from 'react-router-dom'
import {ConfirmDialog} from '../components'

type UnsavedChangesGuardProps = {
  /** Block navigation / warn on tab close while this is true. */
  when: boolean
}

/**
 * Warns before leaving with unsaved changes:
 * - In-app navigation is caught with `useBlocker` (a confirm dialog).
 * - Tab close / reload is caught with `beforeunload` (the browser's own prompt).
 *
 * `useBlocker` reads the data-router context, which the app provides via
 * `createBrowserRouter` + `RouterProvider` in main.tsx — so it works even though
 * the routes themselves are declared with descendant <Routes> in App.tsx.
 */
export const UnsavedChangesGuard = ({when}: UnsavedChangesGuardProps): React.ReactElement | null => {
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({currentLocation, nextLocation}) =>
        when && currentLocation.pathname !== nextLocation.pathname,
      [when],
    ),
  )

  useEffect(() => {
    if (!when) return
    const handler = (event: BeforeUnloadEvent): void => {
      event.preventDefault()
      // Legacy browsers require returnValue to be set to trigger the prompt.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [when])

  if (blocker.state !== 'blocked') return null

  return (
    <ConfirmDialog
      open
      variant="danger"
      title="Cambios sin guardar"
      message="Tienes cambios sin guardar. ¿Salir igualmente?"
      confirmLabel="Salir sin guardar"
      cancelLabel="Seguir editando"
      onConfirm={() => blocker.proceed?.()}
      onCancel={() => blocker.reset?.()}
    />
  )
}
